import styled, { css } from 'styled-components';
import { jdContentTypography } from '../../theme/jdContentTypography';

// ── Color tokens ──────────────────────────────────────────────────────────────
const PINK_BG      = 'rgba(255, 0, 167, 0.08)';
const PINK_BORDER  = '#ff00a7';
const GREEN_BG     = 'rgba(0, 255, 4, 0.08)';
const GREEN_BORDER = '#00ff04';
const GREY_BG      = '#f3f4f6';
const GREY_BORDER  = '#9ca3af';
const NAV_GREY     = '#eaecf0';

// ── Layout ────────────────────────────────────────────────────────────────────

export const EditorHeaderStyled = styled.div`
    padding: 10px 20px;
    background-color: ${NAV_GREY};
    border-bottom: 1px solid ${p => p.theme?.colors?.border ?? '#D8DAE5'};
    display: flex;
    justify-content: space-between;
    align-items: center;
`;


export const HeaderHeadingStyled = styled.h5`
    font-weight: bold;
    margin: 0;
`;

export const HeaderSubtitleStyled = styled.small`
    color: ${p => p.theme?.colors?.gray ?? '#565E70'};
`;

export const BlocksListStyled = styled.div`
    height: calc(100vh - 480px);
    overflow-y: auto;
    padding: 30px;
    background-color: ${p => p.theme?.colors?.white ?? '#ffffff'};
    display: flex;
    flex-direction: column;

    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-thumb { background: ${p => p.theme?.colors?.border ?? '#D8DAE5'}; border-radius: 10px; }
    ${jdContentTypography}
`;

export const FooterActionsStyled = styled.div`
    padding: 10px 20px;
    border-top: 1px solid ${p => p.theme?.colors?.borderLight ?? '#E5E7EB'};
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

export const FooterLeftStyled = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

export const StatusTextStyled = styled.span`
    font-weight: bold;
    color: ${({ $ok, theme }) => ($ok ? (theme?.colors?.success ?? '#28a745') : (theme?.colors?.danger ?? '#DC2626'))};
`;

export const LoadingWrapperStyled = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px;
`;

// ── Block line ────────────────────────────────────────────────────────────────
const lineTypeStyles = {
    needs_work: css`
        background-color: ${PINK_BG};
        border-left-color: ${PINK_BORDER};
    `,
    modified: css`
        background-color: ${GREEN_BG};
        border-left-color: ${GREEN_BORDER};
    `,
    ghost: css`
        background-color: ${GREY_BG};
        border-left-color: ${GREY_BORDER};
        border: 1px dashed ${GREY_BORDER};
        margin: 12px 0;
    `,
};

export const LineStyled = styled.div`
    padding: 2px 8px;
    line-height: 1.6;
    margin: 1px 0;
    background: transparent;
    border-radius: 4px;
    border-left: 3px solid transparent;
    position: relative;
    ${({ $type }) => lineTypeStyles[$type] || ''}
    ${({ $active }) => $active && css`outline: 2px solid #3b82f6; z-index: 10;`}
`;

export const BlockHeaderStyled = styled.div`
    margin-bottom: 2px;
`;

export const BlockBodyStyled = styled.div`
    position: relative;
`;

export const ContentTextStyled = styled.div`
    display: block;
    width: 100%;
    cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

    &:hover { background: ${({ $clickable }) => ($clickable ? 'rgba(0,0,0,0.02)' : 'transparent')}; }

    p { margin: 0; display: block; }

    img {
        max-width: 100%;
        width: 100%;
        height: auto;
        display: block;
        margin: 10px 0;
    }
`;

export const StatusBadgeStyled = styled.span`
    display: inline-block;

    font-weight: bold; 
    line-height: 1.2;
    text-transform: uppercase;
    padding: 2px 6px;
    border-radius: 4px;
    margin-bottom: 6px;

    ${({ $variant }) => $variant === 'review' && css`
        background: #ffebf8; color: ${PINK_BORDER}; border: 1px solid ${PINK_BORDER};
    `}
    ${({ $variant }) => $variant === 'modified' && css`
        background: #ebffec; color: #008f02; border: 1px solid ${GREEN_BORDER};
    `}
    ${({ $variant }) => $variant === 'ghost' && css`
        background: ${GREY_BG}; color: ${p => p.theme?.colors?.dark ?? '#2B3034'}; border: 1px solid ${GREY_BORDER};
    `}
`;

// ── Action popup ──────────────────────────────────────────────────────────────
export const ActionPopupStyled = styled.div`
    position: absolute;
    z-index: 1000;
    background: ${p => p.theme?.colors?.white ?? '#ffffff'};
    border: 1px solid ${p => p.theme?.colors?.borderLight ?? '#E5E7EB'};
    padding: 6px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    display: flex;
    gap: 6px;
    align-items: center;
    top: ${({ $top }) => $top ?? '-42'}px;
    left: 0;
`;

export const PopupCloseStyled = styled.button`
    background: transparent;
    border: none;
    color: ${p => p.theme?.colors?.gray ?? '#565E70'};
    font-size: 18px; 
    margin-left: 5px;
    cursor: pointer;
    &:hover { color: ${p => p.theme?.colors?.dark ?? '#2B3034'}; }
`;


// ── Mini editor ───────────────────────────────────────────────────────────────
export const MiniEditorWrapperStyled = styled.div`
    margin-top: 10px;
    border: 1px solid #3b82f6;
    border-radius: 4px;
    overflow: hidden;

    .md-editor { min-height: 100px; max-height: 250px; }
    .editor-toolbar { padding: 4px; }
`;

export const EditorControlsStyled = styled.div`
    display: flex;
    gap: 8px;
    max-width: 186px; 
    padding: 10px;
    border-top: 1px solid ${p => p.theme?.colors?.borderLight ?? '#E5E7EB'};
    background: ${p => p.theme?.colors?.light ?? '#F7F7F7'};
`;


export const ApproverLoadingStyled = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;
